import Modal from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import TextArea from '@betterfunnel/ui-core/TextArea';
import TextField from '@betterfunnel/ui-core/TextField';
import {
    ContentItem,
    Form,
    LeadsDynamicPage,
    SectionData,
} from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import { TagIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import {
    collection,
    getDocs,
    onSnapshot,
    query,
    QueryDocumentSnapshot,
    where,
} from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DashboardContext } from '../contexts/DashboardContext';
import { DynamicPageContext } from '../contexts/DynamicPageContext';
import { UserContext } from '../contexts/UserContext';
import {
    CreatableWebsiteSectionType,
    createWebsiteSection,
} from '../services/WebsitesService';
import { emptyConverter } from '../utils';

interface Props {
  websiteId: string;
  open: boolean;
  section?: Partial<SectionData>;
  onClose: () => void;
  onAdd: (section: SectionData) => void;
}

interface AddSectionApprovalModalProps {
  type: CreatableWebsiteSectionType;
  open: boolean;
  onClose: () => void;
  onAdd: (prompt?: string, params?: Record<string, any>) => Promise<void>;
}

function InnerAddSectionApprovalModal({
  type,
  open,
  onClose,
  onAdd,
}: AddSectionApprovalModalProps) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [prompt, setPrompt] = useState<string>();
  const [params, setParams] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [comparisonCategories, setComparisonCategories] =
    useState<ContentItem[]>();
  const [forms, setForms] = useState<QueryDocumentSnapshot<Form>[]>();

  useEffect(() => {
    if (!selectedAccount) return;
    if (type !== 'comparisonList' && type !== 'comparisonTopItems') return;
    if (comparisonCategories) return;

    const unsubscribe = onSnapshot(
      query(
        collection(selectedAccount.ref, 'contents'),
        where('status', '==', 'active'),
        where('type', '==', 'comparisonCategory'),
      ).withConverter(emptyConverter<ContentItem>()),
      (items) => {
        setComparisonCategories(items.docs.map((d) => d.data()));
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, comparisonCategories, type]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (type !== 'form' && type !== 'contactUs') return;
    if (forms) return;

    getDocs(
      query(
        collection(selectedAccount.ref, 'forms'),
        where('status', '==', 'active'),
      ).withConverter(emptyConverter<Form>()),
    ).then((items) => {
      setForms(items.docs);
    });
  }, [selectedAccount, type, forms]);

  const onAddClick = useCallback(() => {
    if (!selectedAccount || !selectedDynamicPage) return;
    if (!open) return;

    const selectedDynamicPageData = selectedDynamicPage.data();

    if (
      (type === 'comparisonList' || type === 'comparisonTopItems') &&
      !params.categoryId
    ) {
      setError(
        'Please select a category, if you do not have any in the list, go to the Content page',
      );
      return;
    }

    if (type === 'form' || type === 'contactUs') {
      const selectedFormId =
        params.formId ||
        ('formId' in selectedDynamicPageData && selectedDynamicPageData.formId);

      if (!selectedFormId) {
        setError(
          'Please select a form, if you do not have any in the list, go to the Forms page',
        );
        return;
      }
    }

    if (
      type === 'trustpilot' &&
      (!params.templateId || !params.businessUnitId || !params.domain)
    ) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);

    return onAdd(prompt, params)
      .then(() => {
        setError(undefined);
        setParams({});
        setPrompt(undefined);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError(e.message || e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedAccount, selectedDynamicPage, open, type, params, onAdd, prompt]);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setParams({});
    setPrompt(undefined);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
    >
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Would you like to add some context?
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        {type === 'comparisonList' || type === 'comparisonTopItems' ? (
          <Select<ContentItem | null>
            title="Category"
            selected={
              comparisonCategories?.find((c) => c.id === params.categoryId) ||
              null
            }
            onChange={(e) => {
              setParams((p) => ({
                ...p,
                categoryId: e?.id,
                categoryName: e?.name,
              }));
            }}
            loading={!comparisonCategories}
            options={
              comparisonCategories?.map((category) => ({
                key: category.id,
                value: category,
                child: (
                  <span className="flex items-center gap-2">
                    <TagIcon className="h-4 w-4" />
                    {category.name}
                  </span>
                ),
              })) || []
            }
          />
        ) : (
          <></>
        )}
        {type === 'form' || type === 'contactUs' ? (
          <Select<QueryDocumentSnapshot<Form> | null>
            title="Form"
            selected={
              forms?.find(
                (f) =>
                  f.id ===
                  (params.formId ||
                    (selectedDynamicPage?.data() as LeadsDynamicPage)?.formId),
              ) || null
            }
            onChange={(e) => {
              setParams((p) => ({
                ...p,
                formId: e?.id,
              }));
            }}
            loading={!forms}
            options={
              forms?.map((form) => ({
                key: form.id,
                value: form,
                child: form.data().name,
              })) || []
            }
          />
        ) : (
          <></>
        )}
        {type === 'trustpilot' ? (
          <>
            <TextField
              title="Template Id"
              value={params.templateId}
              onChange={(e) => {
                setParams((p) => ({
                  ...p,
                  templateId: e.target.value,
                }));
              }}
            />
            <TextField
              title="Business Unit Id"
              value={params.businessUnitId}
              onChange={(e) => {
                setParams((p) => ({
                  ...p,
                  businessUnitId: e.target.value,
                }));
              }}
            />
            <TextField
              title="Domain"
              value={params.domain}
              onChange={(e) => {
                setParams((p) => ({
                  ...p,
                  domain: e.target.value,
                }));
              }}
            />
          </>
        ) : (
          <></>
        )}
        <TextArea
          title="Prompt (optional)"
          placeholder="Write your instructions here..."
          value={prompt ?? ''}
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
        />
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}

export function AddSectionApprovalModal({
  websiteId,
  section,
  open,
  onClose,
  onAdd,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);
  const { variants } = useContext(DynamicPageContext);

  const variant = useMemo(
    () => variants?.find((v) => v.data().websiteIds.includes(websiteId)),
    [variants, websiteId],
  );

  const createSection = useCallback(
    (
      type: CreatableWebsiteSectionType,
      prompt?: string,
      params?: Record<string, any>,
    ) => {
      if (!selectedAccount || !selectedDynamicPage) return;
      if (!variant) {
        throw new Error('No variant found');
      }

      return createWebsiteSection(
        selectedAccount.id,
        selectedDynamicPage.id,
        variant.id,
        websiteId,
        { ...section, type },
        prompt,
        params,
      ).then((res) => {
        onAdd(res.section);
      });
    },
    [selectedAccount, selectedDynamicPage, variant, websiteId, section, onAdd],
  );

  return (
    <InnerAddSectionApprovalModal
      type={(section?.type || 'hero') as CreatableWebsiteSectionType}
      open={open}
      onClose={onClose}
      onAdd={async (prompt, params) => {
        return createSection(
          (section?.type || 'hero') as CreatableWebsiteSectionType,
          prompt,
          params,
        );
      }}
    />
  );
}
